/**
 * Escalation System Types and Interfaces
 * For Boris Sales Bot Frontend Integration
 */

// Type definitions for escalation system (using JSDoc for better IDE support)

/**
 * @typedef {Object} EscalationField
 * @property {string} name - Field name
 * @property {string} label - Display label
 * @property {'text'|'email'|'select'|'textarea'} type - Input type
 * @property {boolean} required - Whether field is required
 * @property {string[]} [options] - Options for select fields
 * @property {number} [max_length] - Maximum character length
 */

/**
 * @typedef {Object} EscalationSchema
 * @property {EscalationField[]} fields - Form fields configuration
 */

/**
 * @typedef {Object} EscalationSignal
 * @property {boolean} escalation_required - Whether escalation is needed
 * @property {EscalationSchema} escalation_schema - Form configuration
 */

/**
 * @typedef {Object} ChatResponse
 * @property {string} answer - Bot response message
 * @property {Object} state - Conversation state
 * @property {Object} ui - UI components
 * @property {string} session_id - Session identifier
 * @property {boolean} session_updated - Whether session was updated
 * @property {boolean} requires_followup - Whether followup is needed
 * @property {EscalationSignal} [signals] - Escalation signals
 */

/**
 * @typedef {Object} EscalationFormData
 * @property {string} session_id - Session ID
 * @property {string} contact_name - User's name
 * @property {string} contact_email - User's email
 * @property {string} issue_type - Type of issue
 * @property {string} severity - Issue severity
 * @property {string} summary - Brief summary
 * @property {string} details - Detailed description
 * @property {string} [preferred_contact] - Preferred contact method
 * @property {string} [reason] - Escalation reason
 */

/**
 * @typedef {Object} EscalationResponse
 * @property {string} ticket_id - Generated ticket ID
 * @property {string} session_id - Session ID
 * @property {string} status - Ticket status
 * @property {string} created_at - Creation timestamp
 * @property {string} contact_name - User's name
 * @property {string} contact_email - User's email
 * @property {string} issue_type - Issue type
 * @property {string} severity - Issue severity
 * @property {string} summary - Issue summary
 * @property {string} details - Issue details
 * @property {string} [preferred_contact] - Preferred contact method
 */

// Default escalation schema for fallback
export const DEFAULT_ESCALATION_SCHEMA = {
  fields: [
    {
      name: 'contact_name',
      label: 'Your name',
      type: 'text',
      required: true
    },
    {
      name: 'contact_email',
      label: 'Email',
      type: 'email',
      required: true
    },
    {
      name: 'issue_type',
      label: 'Issue type',
      type: 'select',
      options: ['Billing', 'Technical', 'Account', 'Sales', 'Other'],
      required: true
    },
    {
      name: 'severity',
      label: 'Severity',
      type: 'select',
      options: ['P4 - Low', 'P3 - Normal', 'P2 - High', 'P1 - Critical'],
      required: true
    },
    {
      name: 'summary',
      label: 'Short summary',
      type: 'text',
      required: true,
      max_length: 140
    },
    {
      name: 'details',
      label: 'Details',
      type: 'textarea',
      required: true
    },
    {
      name: 'preferred_contact',
      label: 'Preferred contact',
      type: 'select',
      options: ['Email', 'Phone', 'WhatsApp'],
      required: false
    }
  ]
};

// Utility functions for escalation
export const EscalationUtils = {
  /**
   * Validate form data against schema
   * @param {EscalationFormData} formData 
   * @param {EscalationSchema} schema 
   * @returns {Object} validation result
   */
  validateForm: (formData, schema) => {
    const errors = {};
    let isValid = true;

    schema.fields.forEach(field => {
      const value = formData[field.name];
      
      // Required field validation
      if (field.required && (!value || value.trim() === '')) {
        errors[field.name] = `${field.label} is required`;
        isValid = false;
      }

      // Email validation
      if (field.type === 'email' && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          errors[field.name] = 'Please enter a valid email address';
          isValid = false;
        }
      }

      // Max length validation
      if (field.max_length && value && value.length > field.max_length) {
        errors[field.name] = `Maximum ${field.max_length} characters allowed`;
        isValid = false;
      }
    });

    return { isValid, errors };
  },

  /**
   * Sanitize form data
   * @param {EscalationFormData} formData 
   * @returns {EscalationFormData} sanitized data
   */
  sanitizeFormData: (formData) => {
    const sanitized = {};
    for (const [key, value] of Object.entries(formData)) {
      if (typeof value === 'string') {
        // Remove potentially dangerous characters and trim
        sanitized[key] = value
          .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
          .replace(/<[^>]*>/g, '')
          .trim();
      } else {
        sanitized[key] = value;
      }
    }
    return sanitized;
  },

  /**
   * Generate random ticket ID format
   * @returns {string} ticket ID
   */
  generateTicketId: () => {
    const prefix = 'BSB';
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.random().toString(36).substring(2, 5).toUpperCase();
    return `${prefix}-${timestamp}-${random}`;
  }
};

export default {
  DEFAULT_ESCALATION_SCHEMA,
  EscalationUtils
};
